import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const DiscountDiscrepancyDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showOrderGuid, setShowOrderGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('amount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const toggleRowExpanded = (rowKey) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  // Get friendly type name
  const getTypeName = (discrepancyType) => {
    switch (discrepancyType) {
      case 'ITEM_DISCREPANCY':
        return 'Item Level';
      case 'CHECK_DISCREPANCY':
        return 'Check Level';
      default:
        return discrepancyType || 'Unknown';
    }
  };

  // Filter data based on column filters
  const filteredDetails = useMemo(() => {
    if (!details.length) return details;

    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;

    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        let cellValue;
        if (columnId === 'discrepancyType') {
          // Match against the friendly type name
          cellValue = getTypeName(row[columnId]).toLowerCase();
        } else {
          cellValue = String(row[columnId] ?? '').toLowerCase();
        }
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amount and orderNumber
      if (orderBy === 'amount' || orderBy === 'orderNumber') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId) {
      fetchDetails();
    }
  }, [open, businessDate, storeId]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);
      setExpandedRows({});

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/discount-discrepancy-details', {
        params: {
          businessDate,
          storeId,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching discount discrepancy details:', err);
      setError('Failed to load discount discrepancy details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate totals from filtered data
  const calculatedTotal = filteredDetails.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
  const uniqueOrders = new Set(filteredDetails.map((d) => d.orderGuid)).size;
  const itemDiscrepancies = filteredDetails.filter((d) => d.discrepancyType === 'ITEM_DISCREPANCY').length;
  const checkDiscrepancies = filteredDetails.filter((d) => d.discrepancyType === 'CHECK_DISCREPANCY').length;

  // Check if any filters are active
  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Order #': item.orderNumber,
      'Type': getTypeName(item.discrepancyType),
      'Description': item.discountName || '',
      'Discrepancy Amount': parseFloat(item.amount) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Discount Discrepancies');
    XLSX.writeFile(wb, `discount-discrepancies-${storeId}-${businessDate}.xlsx`);
  };

  // Expandable row content - shows detailed breakdown
  const ExpandedRowContent = ({ detail }) => {
    const isItemDiscrepancy = detail.discrepancyType === 'ITEM_DISCREPANCY';

    const itemPrice = parseFloat(detail.itemGrossSale) || 0;
    const discrepancyAmount = parseFloat(detail.amount) || 0;
    const orderGrossSales = parseFloat(detail.orderGrossSales) || 0;

    // For item discrepancy: total discount applied = item price + excess
    // Because if a $30 item has a $33 discrepancy, that means $63 in discounts were applied
    const totalDiscountApplied = itemPrice + discrepancyAmount;

    return (
      <Box sx={{ py: 2, px: 3, backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Left side - The Math */}
          <Box sx={{ flex: 1.2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalOfferIcon sx={{ fontSize: 16, color: 'warning.main' }} />
              {isItemDiscrepancy ? 'Item Discount Calculation' : 'Check Discount Calculation'}
            </Typography>

            {isItemDiscrepancy ? (
              // ITEM DISCREPANCY - show the specific item math
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">
                    {detail.itemName || 'Item'} (price):
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatCurrency(itemPrice)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'error.main' }}>
                  <Typography variant="body2">
                    Discount applied to this item:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    -{formatCurrency(totalDiscountApplied)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, backgroundColor: '#fff3e0', p: 1, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Excess (discount &gt; price):
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'warning.dark' }}>
                    {formatCurrency(discrepancyAmount)}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {formatCurrency(totalDiscountApplied)} discount − {formatCurrency(itemPrice)} item = {formatCurrency(discrepancyAmount)} excess
                </Typography>
              </Box>
            ) : (
              // CHECK DISCREPANCY - show check-level math
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">Order sub-total:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {formatCurrency(orderGrossSales)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, color: 'error.main' }}>
                  <Typography variant="body2">Check discount applied:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    -{formatCurrency(orderGrossSales + discrepancyAmount)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, backgroundColor: '#ffebee', p: 1, borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Excess (discount &gt; order):
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'error.dark' }}>
                    {formatCurrency(discrepancyAmount)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Right side - Explanation */}
          <Box sx={{ flex: 1, pl: 3, borderLeft: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              What This Means
            </Typography>

            {detail.discountName && (
              <Box sx={{ mb: 1.5, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Discount name:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {detail.discountName}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isItemDiscrepancy ? (
                <>
                  A {formatCurrency(totalDiscountApplied)} discount was applied to an item that only cost {formatCurrency(itemPrice)}.
                  The {formatCurrency(discrepancyAmount)} excess cannot reduce the item below $0.
                </>
              ) : (
                <>
                  A check-level discount exceeded the remaining order balance.
                  This often happens when item-level discounts already reduced the order, then an additional check discount was applied.
                </>
              )}
            </Typography>

            <Box sx={{ backgroundColor: '#e3f2fd', p: 1.5, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'primary.dark', mb: 0.5 }}>
                GL Treatment
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The {formatCurrency(discrepancyAmount)} excess is credited to <strong>670001</strong> (Over/Short) to balance the full discount amount debited to the discount accounts.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '85vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            Discount Discrepancy Details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {memoDetail} - Store {storeId} - {businessDate}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No discount discrepancies found for this entry.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              These orders had discounts that exceeded the order/item value. This typically occurs when multiple comps are applied to the same item.
            </Alert>

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip
                label={`${itemDiscrepancies} Item Level`}
                color="warning"
                variant="outlined"
                size="small"
              />
              <Chip
                label={`${checkDiscrepancies} Check Level`}
                color="error"
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Total: ${formatCurrency(calculatedTotal)}`}
                variant="outlined"
                size="small"
              />
            </Box>

            <TableContainer sx={{ maxHeight: 450, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} />
                    {showOrderGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'orderGuid'}
                          direction={orderBy === 'orderGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('orderGuid')}
                        >
                          Order GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'orderNumber'}
                        direction={orderBy === 'orderNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('orderNumber')}
                      >
                        Order #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'discrepancyType'}
                        direction={orderBy === 'discrepancyType' ? order : 'asc'}
                        onClick={() => handleRequestSort('discrepancyType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'amount'}
                        direction={orderBy === 'amount' ? order : 'asc'}
                        onClick={() => handleRequestSort('amount')}
                      >
                        Discrepancy Amount
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  <TableRow>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }} />
                    {showOrderGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="Filter..."
                          value={columnFilters['orderGuid'] || ''}
                          onChange={(e) => handleColumnFilterChange('orderGuid', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                            '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                            minWidth: 80,
                            width: '100%',
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters['orderGuid'] && (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => clearColumnFilter('orderGuid')} sx={{ p: 0.25 }}>
                                  <ClearIcon sx={{ fontSize: '0.875rem' }} />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['orderNumber'] || ''}
                        onChange={(e) => handleColumnFilterChange('orderNumber', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['orderNumber'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('orderNumber')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['discrepancyType'] || ''}
                        onChange={(e) => handleColumnFilterChange('discrepancyType', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['discrepancyType'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('discrepancyType')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Filter..."
                        value={columnFilters['amount'] || ''}
                        onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { backgroundColor: 'white', fontSize: '0.75rem' },
                          '& .MuiOutlinedInput-input': { py: 0.5, px: 1 },
                          minWidth: 80,
                          width: '100%',
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon sx={{ fontSize: '0.875rem', color: 'action.disabled' }} />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters['amount'] && (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => clearColumnFilter('amount')} sx={{ p: 0.25 }}>
                                <ClearIcon sx={{ fontSize: '0.875rem' }} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => {
                    const rowKey = `${detail.orderGuid}-${detail.checkGuid}-${detail.itemGuid || index}`;
                    const isExpanded = expandedRows[rowKey];

                    return (
                      <React.Fragment key={rowKey}>
                        <TableRow
                          sx={{
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                            cursor: 'pointer',
                          }}
                          onClick={() => toggleRowExpanded(rowKey)}
                        >
                          <TableCell sx={{ p: 0, textAlign: 'center' }}>
                            <IconButton size="small" sx={{ p: 0.25 }}>
                              {isExpanded ? (
                                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </TableCell>
                          {showOrderGuid && (
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {detail.orderGuid || '-'}
                            </TableCell>
                          )}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ReceiptIcon sx={{ fontSize: 16, color: 'action.active' }} />
                              {detail.orderNumber || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getTypeName(detail.discrepancyType)}
                              size="small"
                              variant="outlined"
                              color={detail.discrepancyType === 'ITEM_DISCREPANCY' ? 'warning' : 'error'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'warning.dark', fontFamily: 'monospace' }}>
                            {formatCurrency(detail.amount)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ p: 0, border: 0 }} colSpan={showOrderGuid ? 5 : 4}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <ExpandedRowContent detail={detail} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} discrepanc{filteredDetails.length !== 1 ? 'ies' : 'y'} across {uniqueOrders} order{uniqueOrders !== 1 ? 's' : ''}
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showOrderGuid}
                      onChange={(e) => setShowOrderGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Order GUID</Typography>}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Click a row to see details
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default DiscountDiscrepancyDialog;
